import type {
  AnalyserType,
  ChromatogramFile,
  GcColumnFamily,
  InstrumentCatalogEntry,
  InstrumentProfile,
  InstrumentVendor,
} from "@/utils/types/instrument";

export const INSTRUMENT_VENDORS: InstrumentVendor[] = [
  { id: "Agilent", label: "Agilent" },
  { id: "Thermofisher", label: "Thermo Fisher" },
];

export const ANALYSER_TYPES: AnalyserType[] = [
  { id: "single_quadrupole", label: "Single quadrupole", short: "Single Q", resolution: "Unit-mass" },
  { id: "triple_quadrupole", label: "Triple quadrupole", short: "Triple Q", resolution: "Unit-mass" },
  { id: "q_tof", label: "Quadrupole–TOF", short: "Q-TOF", resolution: "High-resolution" },
  { id: "orbitrap", label: "Orbitrap", short: "Orbitrap", resolution: "High-resolution" },
  { id: "time_of_flight", label: "Time of Flight", short: "TOF", resolution: "High-resolution" },
];

export const INSTRUMENT_CATALOG: InstrumentCatalogEntry[] = [
  { id: 42, label: "5977B/C GC-MSD", family: "5900 MSD", vendor: "Agilent", analyser: "single_quadrupole", platform: "GC-MS" },
  { id: 43, label: "7000D GC/TQ", family: "7000 Series", vendor: "Agilent", analyser: "triple_quadrupole", platform: "GC-MS" },
  { id: 44, label: "7010D GC/TQ", family: "7000 Series", vendor: "Agilent", analyser: "triple_quadrupole", platform: "GC-MS" },
  { id: 45, label: "7250 GC/Q-TOF", family: "7250 Q-TOF", vendor: "Agilent", analyser: "q_tof", platform: "GC-MS" },
  { id: 24, label: "6120 LC/MSD", family: "6100 Series", vendor: "Agilent", analyser: "single_quadrupole", platform: "LC-MS" },
  { id: 29, label: "6460 LC/TQ", family: "6400 Series", vendor: "Agilent", analyser: "triple_quadrupole", platform: "LC-MS" },
  { id: 38, label: "6546 Q-TOF", family: "6500 Series Q-TOF", vendor: "Agilent", analyser: "q_tof", platform: "LC-MS" },
  { id: 41, label: "Revident LC/Q-TOF", family: "Revident", vendor: "Agilent", analyser: "q_tof", platform: "LC-MS" },
  { id: 23, label: "ISQ 7610 GC-MS", family: "ISQ", vendor: "Thermofisher", analyser: "single_quadrupole", platform: "GC-MS" },
  { id: 4, label: "Orbitrap Exploris GC 240", family: "Orbitrap Exploris", vendor: "Thermofisher", analyser: "orbitrap", platform: "GC-MS" },
  { id: 3, label: "Orbitrap Exploris 480", family: "Orbitrap Exploris", vendor: "Thermofisher", analyser: "orbitrap", platform: "LC-MS" },
  { id: 12, label: "Orbitrap Astral", family: "Orbitrap Astral", vendor: "Thermofisher", analyser: "orbitrap", platform: "LC-MS" },
  { id: 17, label: "TSQ Quantiva", family: "TSQ", vendor: "Thermofisher", analyser: "triple_quadrupole", platform: "LC-MS" },
];

export const GC_COLUMN_FAMILIES: GcColumnFamily[] = [
  { id: 18, family: "DB-WAX", polarity: "Polar", phase: "100% polyethylene glycol", typical: "Aroma volatiles, alcohols, polar flavors" },
  { id: 20, family: "DB-FFAP", polarity: "Polar", phase: "Nitroterephthalic-acid modified PEG", typical: "Free fatty acids, acidic volatiles" },
  { id: 19, family: "DB-5", polarity: "Low", phase: "95% dimethyl polysiloxane / 5% diphenyl", typical: "General-purpose, broad screening" },
  { id: 12, family: "DB-1", polarity: "Non-polar", phase: "100% dimethyl polysiloxane", typical: "Hydrocarbons, non-polar volatiles" },
  { id: 14, family: "DB-35", polarity: "Mid-polar", phase: "65% dimethyl polysiloxane / 35% diphenyl", typical: "Pesticides, mid-polarity targets" },
  { id: 2, family: "DB-17", polarity: "Mid-polar", phase: "50% dimethyl polysiloxane / 50% diphenyl", typical: "Drugs of abuse, aromatic compounds" },
  { id: 1, family: "DB-23", polarity: "Polar", phase: "50% dimethyl polysiloxane / 50% cyanopropyl", typical: "FAMEs, cis/trans fatty acid separation" },
  { id: 4, family: "DB-FATWAX", polarity: "Polar", phase: "30% dimethyl / 70% cyanopropyl polysiloxane", typical: "FAMEs, dietary lipids" },
  { id: 10, family: "DB-1701", polarity: "Mid-polar", phase: "86% dimethyl polysiloxane / 14% cyanopropylphenyl", typical: "Pesticides, flavors, terpenoids" },
  { id: 15, family: "DB-XLB", polarity: "Low", phase: "Proprietary phenylene-bridged dimethyl polysiloxane", typical: "Pesticides residue, extended thermal range" },
];

export const INSTRUMENT_TAGS_POOL: string[] = [
  "QC",
  "Hi-res",
  "Targeted",
  "Daily-driver",
  "Reserve required",
  "Method dev",
];

export const USER_INSTRUMENTS: InstrumentProfile[] = [
  {
    id: "inst-mango-qc",
    alias: "Mango QC line",
    instrumentId: 42,
    column: { id: 18, length_m: 30, id_mm: 0.25, film_um: 0.25 },
    inlet: { carrier: "Helium", flow_ml_min: 1.2, temp_C: 250, mode: "Splitless", vol_uL: 1.0 },
    oven: [
      { kind: "initial", temp: 40, hold: 2 },
      { kind: "ramp", rate: 8, target: 240, hold: 5 },
    ],
    ms: { ionization: "EI", sourceTemp_C: 230, energy_eV: 70, scanLo: 35, scanHi: 450, mode: "Full scan" },
    methodology: {
      notes:
        "Sample prep — 5 g homogenate, saline matrix, centrifuged 10 min @ 4 °C. SPME, DVB/CAR/PDMS fiber, 60 min @ 50 °C, 250 rpm. Solvent delay 3.0 min; daily tune required.",
    },
    alkaneLadder: [
      { name: "alkane_C7_C30_mango_qc.raw", cMin: 7, cMax: 30, peaks: 24, uploaded: "2026-03-04" },
    ],
    tags: ["QC", "Daily-driver"],
    notes: "Routine QC for mango/tropical-fruit volatiles. Calibrated against alphonso reference 2026-Q1.",
    createdAt: "2026-03-02",
    lastUsedAt: "2026-05-15",
    chromatograms: [
      { id: "ch-001", name: "alphonso_mango_lot_24A.raw", format: "raw", peaks: 142, sizeMb: 18.4, uploadedAt: "2026-05-08", ingredient: "Alphonso mango concentrate · Lot 24A" },
      { id: "ch-002", name: "alphonso_mango_lot_24B.raw", format: "raw", peaks: 138, sizeMb: 17.9, uploadedAt: "2026-05-09", ingredient: "Alphonso mango concentrate · Lot 24B" },
      { id: "ch-003", name: "ataulfo_mango_ref_v3.mzML", format: "mzML", peaks: 156, sizeMb: 22.6, uploadedAt: "2026-05-11", ingredient: null },
      { id: "ch-004", name: "mango_pulp_HD_2204.raw", format: "raw", peaks: 134, sizeMb: 16.8, uploadedAt: "2026-05-13", ingredient: "Mango pulp · Hindustan Foods Lot 2204" },
    ],
  },
  {
    id: "inst-aroma-hires",
    alias: "Aroma fingerprint (hi-res)",
    instrumentId: 45,
    column: { id: 19, length_m: 30, id_mm: 0.25, film_um: 0.25 },
    inlet: { carrier: "Helium", flow_ml_min: 1.0, temp_C: 280, mode: "Split 1:20", vol_uL: 1.0 },
    oven: [
      { kind: "initial", temp: 40, hold: 3 },
      { kind: "ramp", rate: 5, target: 180, hold: 0 },
      { kind: "ramp", rate: 15, target: 300, hold: 6 },
    ],
    ms: { ionization: "EI", sourceTemp_C: 280, energy_eV: 70, scanLo: 40, scanHi: 600, mode: "Full scan · 25k res" },
    methodology: {
      notes:
        "High-resolution fingerprinting; novel reference foods only. Q-TOF tuned weekly. Internal standard cocktail required for every run.",
    },
    alkaneLadder: [],
    tags: ["Hi-res", "Reserve required", "Method dev"],
    notes: "High-resolution fingerprinting for novel reference foods. Reserve booking required.",
    createdAt: "2026-01-18",
    lastUsedAt: "2026-05-12",
    chromatograms: [
      { id: "ch-101", name: "gariguette_2026_pool_3reps.raw", format: "raw", peaks: 312, sizeMb: 64.8, uploadedAt: "2026-05-07", ingredient: "Gariguette strawberry · Target reference" },
      { id: "ch-102", name: "hojicha_2024_batch7.mzML", format: "mzML", peaks: 218, sizeMb: 41.2, uploadedAt: "2026-04-22", ingredient: "Hojicha extract · Batch 7" },
    ],
  },
  {
    id: "inst-dairy-tq",
    alias: "Dairy lactones (TQ)",
    instrumentId: 43,
    column: { id: 20, length_m: 60, id_mm: 0.25, film_um: 0.25 },
    inlet: { carrier: "Helium", flow_ml_min: 1.4, temp_C: 240, mode: "Splitless", vol_uL: 2.0 },
    oven: [
      { kind: "initial", temp: 50, hold: 1 },
      { kind: "ramp", rate: 10, target: 240, hold: 8 },
    ],
    ms: { ionization: "EI", sourceTemp_C: 250, energy_eV: 70, scanLo: 50, scanHi: 350, mode: "MRM · 18 transitions" },
    methodology: {
      notes: "Targeted MRM for γ- and δ-lactones in dairy. Method developed with Robertet 2025-11. Run-conditions handed off in shared method file.",
    },
    alkaneLadder: [
      { name: "alkane_C7_C30_dairy.raw", cMin: 7, cMax: 30, peaks: 24, uploaded: "2025-11-08" },
    ],
    tags: ["Targeted"],
    notes: "Targeted MRM for γ- and δ-lactones in dairy. Method developed with Robertet 2025-11.",
    createdAt: "2025-11-04",
    lastUsedAt: "2026-05-10",
    chromatograms: [
      { id: "ch-201", name: "cream_12pct_lot_C0418.raw", format: "raw", peaks: 47, sizeMb: 9.2, uploadedAt: "2026-05-10", ingredient: "Cream · 12% MF · Lot C0418" },
    ],
  },
  {
    id: "inst-citrus-screen",
    alias: "Citrus screen",
    instrumentId: 23,
    column: { id: 18, length_m: 30, id_mm: 0.25, film_um: 0.50 },
    inlet: { carrier: "Helium", flow_ml_min: 1.1, temp_C: 230, mode: "Split 1:10", vol_uL: 1.0 },
    oven: [
      { kind: "initial", temp: 45, hold: 2 },
      { kind: "ramp", rate: 6, target: 220, hold: 4 },
    ],
    ms: { ionization: "EI", sourceTemp_C: 230, energy_eV: 70, scanLo: 30, scanHi: 400, mode: "Full scan" },
    methodology: { notes: "Daily-driver method for citrus oils + terpene-heavy extracts." },
    alkaneLadder: [],
    tags: ["Daily-driver"],
    notes: "Daily-driver method for citrus oils + terpene-heavy extracts.",
    createdAt: "2025-08-12",
    lastUsedAt: "2026-05-14",
    chromatograms: [],
  },
];

export const UNASSIGNED_CHROMATOGRAMS: ChromatogramFile[] = [
  { id: "ch-orph-1", name: "yuzu_peel_oil_2024_Q4.raw", format: "raw", peaks: 89, sizeMb: 12.4, uploadedAt: "2026-05-02", ingredient: "Yuzu peel cold-press · 2024-Q4" },
  { id: "ch-orph-2", name: "vanilla_oleoresin_madagascar.mzML", format: "mzML", peaks: 64, sizeMb: 7.1, uploadedAt: "2026-04-29", ingredient: "Vanilla oleoresin · Madagascar" },
];

export const DEFAULT_INSTRUMENT_ID = "inst-mango-qc";

export const findInstrumentCatalog = (id: number | null | undefined): InstrumentCatalogEntry | null =>
  INSTRUMENT_CATALOG.find((x) => x.id === id) ?? null;

export const findColumnFamily = (id: number | null | undefined): GcColumnFamily | null =>
  GC_COLUMN_FAMILIES.find((x) => x.id === id) ?? null;

export const findAnalyser = (id: string | null | undefined): AnalyserType | null =>
  ANALYSER_TYPES.find((x) => x.id === id) ?? null;

export const instrumentVendorAccent = (vendor: string | null | undefined): string => {
  if (vendor === "Agilent") return "var(--fam-citrus)";
  if (vendor === "Thermofisher") return "var(--fam-fruity)";
  return "var(--ink-3)";
};
